import { useMemo } from "react";
import { qrPath } from "./qrPath";

/**
 * Draws a QR code as inline SVG, generated from the string at render time rather
 * than loaded as an image file — so changing `previewConfig.qr.url` changes the
 * code with it, and there's no asset to keep in sync.
 *
 * Rendered dark-on-light with a quiet zone, because that's what phone cameras
 * expect; inverting it to suit the dark page around it would cost scans.
 */
export function QrCode({
  value,
  size = 128,
  margin = 2,
  className,
}: {
  value: string;
  /** Rendered width/height in CSS pixels. */
  size?: number;
  /** Quiet zone around the code, in modules. */
  margin?: number;
  className?: string;
}): React.ReactElement {
  const { path, extent } = useMemo(() => qrPath(value, margin), [value, margin]);

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${extent} ${extent}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`QR code linking to ${value}`}
    >
      <rect width={extent} height={extent} fill="#ffffff" />
      <path d={path} fill="#140a24" />
    </svg>
  );
}
